import React, { useState, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Box,
  Autocomplete
} from '@mui/material';
import { Transaction } from '../../types';
import { useBudget } from '../../contexts/BudgetContext';

interface NewTransactionDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (transaction: Omit<Transaction, '_id' | 'user' | 'createdAt'>) => void;
}

const NewTransactionDialog: React.FC<NewTransactionDialogProps> = ({ open, onClose, onSubmit }) => {
  const { currentBudget } = useBudget();
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    description: '',
    amount: '',
    type: 'expense' as 'income' | 'expense' | 'savings',
    category: '',
    notes: ''
  });

  // Filter categories based on transaction type
  const availableCategories = useMemo(() => {
    if (!currentBudget) return [];
    return currentBudget.categories
      .filter(cat => cat.type === formData.type)
      .map(cat => cat.name);
  }, [currentBudget, formData.type]);

  const handleSubmit = () => {
    if (!formData.description || !formData.amount || !formData.category) return;

    onSubmit({
      date: formData.date,
      description: formData.description,
      amount: Number(formData.amount),
      type: formData.type,
      category: formData.category,
      notes: formData.notes
    });

    // Reset form
    setFormData({
      date: new Date().toISOString().split('T')[0],
      description: '',
      amount: '',
      type: 'expense',
      category: '',
      notes: ''
    });
  };

  const handleTypeChange = (type: 'income' | 'expense' | 'savings') => {
    setFormData(prev => ({
      ...prev,
      type,
      category: '' // Reset category when type changes
    }));
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add New Transaction</DialogTitle>
      <DialogContent>
        <Box display="flex" flexDirection="column" gap={2} mt={1}>
          <TextField
            label="Date"
            type="date"
            value={formData.date}
            onChange={(e) => setFormData({ ...formData, date: e.target.value })}
            required
            InputLabelProps={{
              shrink: true
            }}
          />
          <TextField
            label="Description"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            required
          />
          <TextField
            label="Amount"
            type="number"
            value={formData.amount}
            onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
            required
          />
          <TextField
            select
            label="Type"
            value={formData.type}
            onChange={(e) => handleTypeChange(e.target.value as 'income' | 'expense' | 'savings')}
            required
          >
            <MenuItem value="income">Income</MenuItem>
            <MenuItem value="expense">Expense</MenuItem>
            <MenuItem value="savings">Savings</MenuItem>
          </TextField>
          <Autocomplete
            value={formData.category}
            onChange={(_, newValue) => setFormData({ ...formData, category: newValue || '' })}
            options={availableCategories}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Category"
                required
                error={!formData.category}
                helperText={!formData.category ? 'Please select a category' : ''}
              />
            )}
            freeSolo
            autoComplete
            autoHighlight
            clearOnBlur
            handleHomeEndKeys
          />
          <TextField
            label="Notes"
            value={formData.notes}
            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
            multiline
            rows={3}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleSubmit}
          color="primary"
          disabled={!formData.description || !formData.amount || !formData.category}
        >
          Add Transaction
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NewTransactionDialog; 